import React, { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Building2, Users, Package, FileText, Receipt,
  Settings, Bell, LogOut, Menu, Home, BarChart3, AlertTriangle,
  Clock, DollarSign, MessageSquare, ShieldCheck, Truck, Banknote,
  Inbox, CreditCard, ScrollText, TrendingUp, Layers, ScanLine,
  ChevronRight, ChevronDown, UserCog, FileSpreadsheet, ClipboardList, Tag,
  CheckSquare, ArrowRightLeft, PanelLeftClose, PanelLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_SECTIONS = [
  {
    key: "suppliers", label: "Suppliers", icon: Building2, path: "/suppliers",
    items: [
      { label: "All Suppliers", path: "/suppliers" },
      { label: "Purchase Orders", path: "/purchase-orders" },
      { label: "Supplier Payments", path: "/supplier-payments" },
      { label: "Supplier Credit Notes", path: "/credit-notes" },
      { label: "Quote Requests", path: "/quote-requests" },
    ],
  },
  {
    key: "customers", label: "Customers", icon: Users, path: "/customers",
    items: [
      { label: "All Customers", path: "/customers" },
      { label: "Quotes", path: "/quotes" },
      { label: "Customer Purchase Orders", path: "/customer-purchase-orders" },
      { label: "Invoices", path: "/invoices" },
      { label: "Customer Payments", path: "/customer-payments" },
      { label: "Credit Notes", path: "/credit-notes" },
      { label: "Statements", path: "/statements" },
    ],
  },
  {
    key: "expenses", label: "Expenses", icon: Banknote, path: "/expenses",
    items: [
      { label: "All Expenses", path: "/expenses" },
      { label: "Expense Categories", path: "/expense-categories" },
      { label: "Receipt Scanning", path: "/receipt-scanning" },
      { label: "Pending Approvals", path: "/approvals" },
    ],
  },
  {
    key: "orders", label: "Orders", icon: ClipboardList, path: "/invoices",
    items: [
      { label: "All Orders", path: "/invoices" },
      { label: "Invoices", path: "/invoices" },
      { label: "Delivery Notes", path: "/delivery-notes" },
      { label: "Packing Slips", path: "/packing-slips" },
    ],
  },
  {
    key: "inventory", label: "Inventory", icon: Package, path: "/inventory",
    items: [
      { label: "All Products", path: "/inventory" },
      { label: "Categories", path: "/inventory-categories" },
      { label: "Stock Adjustments", path: "/stock-adjustments" },
      { label: "Low Stock Alerts", path: "/low-stock" },
      { label: "Price Lists", path: "/price-lists" },
      { label: "Volume Discounts", path: "/price-lists" },
    ],
  },
  {
    key: "payments", label: "Payments", icon: DollarSign, path: "/customer-payments",
    items: [
      { label: "Customer Payments", path: "/customer-payments" },
      { label: "Supplier Payments", path: "/supplier-payments" },
      { label: "Payment Capture", path: "/payment-capture" },
      { label: "Payment Batches", path: "/payment-batches" },
      { label: "Aging Reports", path: "/aging-reports" },
    ],
  },
  {
    key: "approvals", label: "Approvals", icon: ShieldCheck, path: "/approvals", hasBadge: true,
    items: [
      { label: "Email Imports", path: "/email-imports" },
      { label: "Expense Approvals", path: "/approvals" },
      { label: "Refund Approvals", path: "/approvals" },
      { label: "Customer PO Approvals", path: "/approvals" },
      { label: "Supplier Payment Approvals", path: "/approvals" },
    ],
  },
  {
    key: "reports", label: "Reports", icon: BarChart3, path: "/reports",
    items: [
      { label: "Sales Reports", path: "/reports/sales" },
      { label: "Purchase Reports", path: "/reports/purchases" },
      { label: "Inventory Reports", path: "/reports/inventory" },
      { label: "Financial Reports", path: "/reports/financial" },
      { label: "Profit Analysis", path: "/reports/profit" },
      { label: "VAT Returns", path: "/vat-returns" },
      { label: "VAT Audit Trail", path: "/vat-audit-trail" },
      { label: "Export Data", path: "/reports/export" },
    ],
  },
];

const BOTTOM_SECTIONS = [
  {
    key: "settings", label: "Settings", icon: Settings, path: "/settings", adminOnly: true,
    items: [
      { label: "Business Settings", path: "/settings" },
      { label: "Dashboard Configuration", path: "/settings/dashboard" },
      { label: "Users & Permissions", path: "/users" },
      { label: "Email Integration", path: "/email-imports" },
      { label: "Payment Capture Config", path: "/payment-capture" },
      { label: "Integrations", path: "/integrations" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState({ approvals: 0 });

  // Sidebar state
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem("sidebar_pinned") === "true"; } catch { return false; }
  });
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [flyout, setFlyout] = useState(null); // { key, top }
  const sidebarRef = useRef(null);
  const hoverTimeout = useRef(null);
  const flyoutTimeout = useRef(null);

  const expanded = pinned || hoverExpanded || mobileOpen;

  const fetchNotifications = useCallback(() => {
    api.get("/notifications").then((r) => setNotifications(r.data.notifications || [])).catch(() => {});
  }, []);
  const fetchBadgeCounts = useCallback(() => {
    api.get("/notifications/counts").then((r) => setBadgeCounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchNotifications(); fetchBadgeCounts();
    const i1 = setInterval(fetchNotifications, 60000);
    const i2 = setInterval(fetchBadgeCounts, 30000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [fetchNotifications, fetchBadgeCounts]);

  // Auto-open the active section when pinned
  useEffect(() => {
    if (pinned) {
      const all = [...NAV_SECTIONS, ...BOTTOM_SECTIONS];
      for (const s of all) {
        if (s.items?.some(item => isActive(item.path))) {
          setOpenSections(prev => ({ ...prev, [s.key]: true }));
          break;
        }
      }
    }
  }, [location.pathname, pinned]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/" || location.pathname.startsWith("/dashboard");
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };
  const isSectionActive = (section) => isActive(section.path) || section.items?.some(i => isActive(i.path));

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    try { localStorage.setItem("sidebar_pinned", String(next)); } catch {}
    if (!next) { setOpenSections({}); }
    setFlyout(null);
  };

  const handleSidebarEnter = () => {
    if (pinned) return;
    clearTimeout(hoverTimeout.current);
    setHoverExpanded(true);
  };
  const handleSidebarLeave = () => {
    if (pinned) return;
    hoverTimeout.current = setTimeout(() => {
      setHoverExpanded(false);
      setFlyout(null);
    }, 300);
  };

  // Pinned mode: toggle inline sub-items
  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Hover mode: position fixed flyout
  const handleSectionMouseEnter = (key, e) => {
    if (pinned) return;
    clearTimeout(flyoutTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({ key, top: rect.top });
  };
  const handleSectionMouseLeave = () => {
    if (pinned) return;
    flyoutTimeout.current = setTimeout(() => setFlyout(null), 200);
  };
  const handleFlyoutEnter = () => { clearTimeout(flyoutTimeout.current); clearTimeout(hoverTimeout.current); };
  const handleFlyoutLeave = () => {
    flyoutTimeout.current = setTimeout(() => setFlyout(null), 200);
  };

  const navTo = (path) => { navigate(path); setMobileOpen(false); setFlyout(null); };

  // Close mobile on outside tap
  useEffect(() => {
    const handler = (e) => {
      if (mobileOpen && sidebarRef.current && !sidebarRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("touchstart", handler); };
  }, [mobileOpen]);

  const renderSection = (section) => {
    const active = isSectionActive(section);
    const isOpen = openSections[section.key];
    const hasItems = section.items?.length > 0;
    const showInlineItems = pinned && isOpen && hasItems;

    return (
      <div key={section.key} className="nav-section-wrapper" data-testid={`nav-section-${section.key}`}
        onMouseEnter={(e) => handleSectionMouseEnter(section.key, e)}
        onMouseLeave={handleSectionMouseLeave}>
        <button
          data-testid={`nav-${section.key}`}
          onClick={() => { if (pinned && hasItems) { toggleSection(section.key); } else { navTo(section.path); } }}
          className={`sidebar-nav-item ${active ? "active" : ""}`}
          title={!expanded ? section.label : undefined}
        >
          <section.icon className="sidebar-nav-icon" strokeWidth={1.5} />
          <span className="sidebar-nav-label">{section.label}</span>
          {section.hasBadge && badgeCounts.approvals > 0 && (
            <span className="sidebar-badge" data-testid="approvals-badge">{badgeCounts.approvals > 99 ? "99+" : badgeCounts.approvals}</span>
          )}
          {hasItems && expanded && (
            pinned
              ? (isOpen ? <ChevronDown className="sidebar-nav-arrow" /> : <ChevronRight className="sidebar-nav-arrow" />)
              : <ChevronRight className="sidebar-nav-arrow" />
          )}
        </button>
        {/* Inline sub-items (pinned mode) */}
        {showInlineItems && (
          <div className="sidebar-inline-sub" data-testid={`inline-sub-${section.key}`}>
            {section.items.map((item, idx) => (
              <button key={`${section.key}-${idx}`} data-testid={`subnav-${section.key}-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => navTo(item.path)}
                className={`sidebar-inline-item ${isActive(item.path) ? "active" : ""}`}>
                <span className="sidebar-inline-dot" />
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Find the hovered section for flyout
  const flyoutSection = flyout ? [...NAV_SECTIONS, ...BOTTOM_SECTIONS].find(s => s.key === flyout.key) : null;

  return (
    <div className={`layout-root ${pinned ? "sidebar-pinned" : ""}`}>
      {mobileOpen && <div className="sidebar-mobile-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <nav ref={sidebarRef} data-testid="sidebar-nav"
        className={`sidebar-container ${expanded ? "expanded" : ""} ${mobileOpen ? "mobile-open" : ""}`}
        onMouseEnter={handleSidebarEnter} onMouseLeave={handleSidebarLeave}>

        {/* Logo + Pin */}
        <div className="sidebar-header-row">
          <div className="sidebar-logo" onClick={() => navTo("/dashboard")}>
            <div className="sidebar-logo-icon"><Home className="w-4 h-4 text-white" strokeWidth={2.5} /></div>
            <span className="sidebar-logo-text" data-testid="brand-logo">SUPPLY HOUSE</span>
          </div>
          {expanded && (
            <button data-testid="sidebar-pin-btn" onClick={togglePin}
              className="sidebar-pin-btn" title={pinned ? "Unpin sidebar" : "Pin sidebar open"}>
              {pinned ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Dashboard */}
        <button data-testid="nav-dashboard" onClick={() => navTo("/dashboard")}
          className={`sidebar-nav-item ${isActive("/dashboard") ? "active" : ""}`}
          title={!expanded ? "Dashboard" : undefined}>
          <LayoutDashboard className="sidebar-nav-icon" strokeWidth={1.5} />
          <span className="sidebar-nav-label">Dashboard</span>
        </button>

        {/* Main Sections */}
        <div className="sidebar-sections">{NAV_SECTIONS.map(s => renderSection(s))}</div>

        {/* Bottom */}
        <div className="sidebar-bottom">
          {BOTTOM_SECTIONS.filter(s => !s.adminOnly || user?.role === "admin").map(s => renderSection(s))}
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{user?.name?.charAt(0)?.toUpperCase() || "U"}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">{user?.role?.replace("_", " ")}</div>
            </div>
          </div>
        </div>
      </nav>

      {/* Fixed-position flyout submenu (hover mode only) */}
      {!pinned && flyout && flyoutSection?.items?.length > 0 && expanded && (
        <div key={flyout.key} className="sidebar-flyout" data-testid={`flyout-${flyout.key}`}
          style={{ top: Math.min(flyout.top, window.innerHeight - (flyoutSection.items.length * 36 + 40)) }}
          onMouseEnter={handleFlyoutEnter} onMouseLeave={handleFlyoutLeave}>
          <div className="sidebar-flyout-header">{flyoutSection.label}</div>
          {flyoutSection.items.map((item, idx) => (
            <button key={`${flyout.key}-${idx}`} data-testid={`subnav-${flyout.key}-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => navTo(item.path)}
              className={`sidebar-flyout-item ${isActive(item.path) ? "active" : ""}`}>
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="layout-main">
        <header className="layout-topbar" data-testid="top-bar">
          <button data-testid="mobile-menu-toggle" className="layout-mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}><Menu className="w-5 h-5" /></button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
                  <Bell className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                  {notifications.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#E63946] rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                      {notifications.length > 9 ? "9+" : notifications.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifications</span>
                  <span className="text-xs text-gray-400 font-normal">{notifications.length} items</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="px-3 py-6 text-center text-gray-400 text-sm">No notifications</div>
                ) : notifications.slice(0, 15).map((n, i) => {
                  const icon = n.type === "invoice_overdue" ? AlertTriangle : n.type === "invoice_due" ? Clock : n.type === "payment_received" ? DollarSign : n.type === "quote_pending" ? FileText : Bell;
                  const color = n.priority === "critical" ? "text-[#E63946]" : n.priority === "warning" ? "text-orange-500" : n.priority === "success" ? "text-green-500" : "text-blue-500";
                  return (
                    <DropdownMenuItem key={i} className="px-3 py-2.5 cursor-pointer" onClick={() => {
                      setNotifOpen(false);
                      if (n.entity_type === "quote_request" && n.entity_id) navigate(`/quote-requests/${n.entity_id}`);
                      else if (n.entity_type === "purchase_order" && n.entity_id) navigate(`/purchase-orders/${n.entity_id}`);
                      else if (n.entity_type && n.entity_id) navigate(`/${n.entity_type === "quote" ? "quotes" : "invoices"}/${n.entity_id}`);
                    }}>
                      <div className="flex gap-2.5 w-full">
                        <div className={`mt-0.5 shrink-0 ${color}`}>{React.createElement(icon, { className: "w-4 h-4" })}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#1F2937] truncate">{n.title}</p>
                          <p className="text-xs text-gray-500 truncate">{n.message}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#E63946] flex items-center justify-center text-white text-xs font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.role?.replace("_", " ")}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="layout-content"><Outlet /></main>
      </div>
    </div>
  );
}
